import React, {useMemo} from 'react';

import * as Analytics from 'analytics/Analytics';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {
  ActionOrigin,
  AnalyticsMetadataForContext,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LookupConfigSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import LookupConfigSummaryChip from 'lookup-config/LookupConfigSummaryChip';
import {getLookupInfoArrayForLookupConfig} from 'lookup-config/LookupConfigUtils';
import {lookupStepsToInfosArray} from 'lookup-config/LookupStepInitializerUtils';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  ROUTER_ERROR_TYPES,
  getFriendlySupportedRouterFieldTypes,
} from 'processes/modals/router-config/RouterConfigConstants';
import {isLookupInvalidForRouting} from 'processes/util/flowchart_config/FlowchartConfigLookupConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ChipSize} from 'ui-library/chips/Chip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import APIUtil from 'util/APIUtil';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {getInvalidLookupForRoutingErrorMessage} from '../../util/flowchart_config/FlowchartConfigLookupConfigUtils';

export interface ConditionalLogicLookupChipProps {
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  initialFunction: DeepPartial<BenchlingExpressionFunction> | null;
  initialLookupConfig: LookupConfigSpec | null;
  inputNode: ProcessGraphBuilderNode;
  flowchartClientId: string;
  flowchartTaskSchema: Schema;
  inputSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | ProcessOutputSchemaGraphQL;
  routerNodeClientId: string;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
}

// exported for testing
export function basePaneRoutingErrorMessage(): JSX.Element {
  const supportedRoutingTypesList = (
    <>
      <p>Field types that can be used for workflow routing:</p>
      <ul>
        {getFriendlySupportedRouterFieldTypes().map((name, index) => (
          <li key={index}>{name}</li>
        ))}
      </ul>
    </>
  );
  return (
    <div>
      This lookup object doesn&apos;t have a{' '}
      <Popover renderContent={() => supportedRoutingTypesList} placement={FloatingPlacement.TOP}>
        <a>supported field type</a>
      </Popover>{' '}
      that can be used for workflow routing.
    </div>
  );
}

export default function ConditionalLogicLookupChip(props: ConditionalLogicLookupChipProps): JSX.Element {
  const lookupStepInfos = useMemo(
    () =>
      props.initialLookupConfig
        ? getLookupInfoArrayForLookupConfig(
            lookupStepsToInfosArray(props.initialLookupConfig.lookupSteps, props.inputSchema, {
              flowchartTaskSchema: props.flowchartTaskSchema,
            })
          )
        : [],
    [props.initialLookupConfig, props.inputSchema, props.flowchartTaskSchema]
  );
  const inputNodeContextKey = props.inputNode.clientId;

  return (
    <LookupConfigSummaryChip
      lookupStepInfos={lookupStepInfos}
      initialLookupConfig={props.initialLookupConfig}
      isEditable={true}
      canEditIfArchived={true}
      customWidthPx={120}
      baseSchema={props.inputSchema}
      firstLookupStepTypes={[
        LookupStepType.SCHEMA_FIELD,
        LookupStepType.PROCESS_TASK,
        LookupStepType.FLOWCHART_PROCESS_TASK,
      ]}
      onLookupChanged={(lookupConfig) => {
        const newParam = {
          expression: {
            type: BenchlingExpressionType.SCHEMA_LOOKUP,
            schema: props.inputSchema.id,
            lookupConfig,
            expression: {
              type: BenchlingExpressionType.CONTEXT_VARIABLE,
              itemType: {
                name: APIUtil.isProcessTaskSchema(props.inputSchema.id)
                  ? LeafType.PROCESS_TASK
                  : LeafType.PROCESS_OUTPUT,
                selectorId: null,
              },
              contextKey: inputNodeContextKey,
            },
          },
        };
        // Updating the lookup config resets the function/operator and RHS value
        const newExpression = {
          type: BenchlingExpressionType.FUNCTION,
          name: undefined,
          parameters: [newParam],
        };

        props.setExpression(newExpression as DeepPartial<BenchlingExpressionFunction>);
      }}
      lookupConfigContext={{
        flowchartTaskSchema: props.flowchartTaskSchema,
      }}
      basePaneLookupObjectValidation={{
        basePaneInvalidLookupObjectFn: isLookupInvalidForRouting,
        onBasePaneSubmissionError: () => {
          Analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.ERROR_MESSAGE_SHOWN_ROUTER_CONFIGURATION, {
            ...standardAnalyticsMetadataToDict(props.analyticsMetadata),
            action_origin: ActionOrigin.WORKFLOWS_ROUTER_NODE_CONFIGURATION_MODAL,
            error_type: ROUTER_ERROR_TYPES.INVALID_LOOKUP,
            node_id: props.routerNodeClientId,
          });
        },
        basePaneInvalidLookupObjectErrorMessage: basePaneRoutingErrorMessage,
      }}
      getLookupConfigValidationError={getInvalidLookupForRoutingErrorMessage}
      chipSize={ChipSize.SMALL}
    />
  );
}
